import { useEffect, useRef } from 'react';
import { didTasksSettle, type TaskCounts } from '@/lib/task-settle';

/**
 * Run ``onSettled`` each time the campaign's tasks settle while the page is open (#6826).
 *
 * ``counts`` is null until task tracking has loaded. Only the counts drive the check, so a new ``onSettled`` on
 * every render never re-fires it (7ec658ff6e).
 */
export function useRefetchOnTasksSettled(counts: TaskCounts | null, onSettled: () => void): void {
  const previous = useRef<TaskCounts | null>(null);
  const latestOnSettled = useRef(onSettled);

  useEffect(() => {
    latestOnSettled.current = onSettled;
  });

  const sumTotal = counts?.sumTotal;
  const taskProcessing = counts?.taskProcessing;

  useEffect(() => {
    if (sumTotal === undefined || taskProcessing === undefined) {
      return;
    }
    const current = { sumTotal, taskProcessing };
    if (didTasksSettle(previous.current, current)) {
      latestOnSettled.current();
    }
    previous.current = current;
  }, [sumTotal, taskProcessing]);
}
